/**
 * The shared chrome every column-settings dialog is built from (spec M1-B5 §6.2, card P-123).
 *
 * monday draws *Customize `<X>` column* with the same seven parts for every type — a 320 px card,
 * a `<Name> column settings` title, 15 px section labels, a 288 × 32 segmented button group,
 * 16 px checkbox rows with an indented subtitle, 1 px dividers and (for the three types that have
 * one) a row of 24 px colour circles. The measurements and their captures are documented on
 * `column-settings.css`; this file is only the markup and the behaviour.
 *
 * **Nothing here knows a column type.** A type's dialog is `columns/<type>/SettingsDialog.tsx`
 * plus one `SettingsDialog:` line in that folder's `defineColumnUi` (CLAUDE.md rule 6) — exactly
 * like its renderer and its editor.
 *
 * Three rules every dialog in the family keeps, asserted in `settings-dialogs.test.tsx`:
 *
 *  1. **An unrecognised key survives a save.** The draft always starts as a copy of the settings
 *     it was handed, and a control writes one key: a board that carries settings this product has
 *     not modelled must not lose them by having its column opened. `useSettingsDraft` is what
 *     makes that the default rather than something each dialog remembers to do.
 *  2. **`Cancel` and `Escape` write nothing.** The host owns `Escape` (`ColumnSettingsHost`);
 *     `Cancel` calls `onCancel`, and neither touches `onCommit`.
 *  3. **`settings_str` in monday's shape, never a shape of ours.** Every control names the key
 *     `packages/shared/src/columns/<type>` declares, which is the key the importer writes and
 *     `/v2` returns.
 */
import { statusColorHex } from '@elizmonday/ui';
import { useState, type ReactNode } from 'react';
import './column-settings.css';

/* ── the draft ────────────────────────────────────────────────────────────── */

/**
 * The settings under edit, and a patcher that merges one key into them.
 *
 * `settings` arrives as `unknown` from the registry and can be `null` for a column monday never
 * gave settings; it is coerced to `{}` so a dialog never has to. Unknown keys ride along in the
 * spread and are committed untouched (rule 1 above).
 */
export function useSettingsDraft<T extends object>(
  settings: T | null | undefined,
): [T, (next: Partial<T>) => void, (next: T) => void] {
  const [draft, setDraft] = useState<T>(() =>
    settings !== null && typeof settings === 'object' ? { ...(settings as T) } : ({} as T),
  );
  const patch = (next: Partial<T>): void => setDraft((current) => ({ ...current, ...next }));
  return [draft, patch, setDraft];
}

/* ── the card ─────────────────────────────────────────────────────────────── */

export interface SettingsFormProps {
  /**
   * monday's own wording, verbatim from the capture: *Rating column settings*,
   * *World Clock column settings*, *Time Tracking column settings* — the type's display name,
   * capitalised as monday capitalises it, then `column settings`.
   */
  readonly title: string;
  /** `<type>-settings`, the id the verifiers and the coverage ledger address the dialog by. */
  readonly testId: string;
  readonly children: ReactNode;
  /** Replaces *Apply* when the type's own dialog names its primary button differently. */
  readonly saveLabel?: string;
  onSave(): void;
  onCancel(): void;
}

export function SettingsForm({
  title,
  testId,
  children,
  saveLabel = 'Apply',
  onSave,
  onCancel,
}: SettingsFormProps) {
  return (
    <form
      className="ez-type-settings"
      data-testid={testId}
      onSubmit={(event) => {
        event.preventDefault();
        onSave();
      }}
    >
      <h2 className="ez-type-settings__title">{title}</h2>
      {children}
      <footer className="ez-type-settings__footer">
        <button type="button" className="ez-type-settings__cancel" data-testid="settings-cancel" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="ez-type-settings__save" data-testid="settings-save">
          {saveLabel}
        </button>
      </footer>
    </form>
  );
}

/**
 * The **primary button's** test id, per type.
 *
 * Every dialog built on this kit names it `settings-save`. Three predate the kit and name it
 * after monday's own wording instead — *Set formula* on the formula builder, *Save* on the two
 * connect-boards dialogs — and round C2's verifier addresses all three by those ids, so they are
 * kept and listed here rather than renamed. `tools/browser/verify-column-settings-local.mjs`
 * carries the same three, with a pointer back to this constant.
 */
export const SETTINGS_SAVE_TESTIDS: Readonly<Record<string, string>> = {
  formula: 'formula-set',
  board_relation: 'relation-save',
  dependency: 'dependency-save',
};

export function settingsSaveTestId(type: string): string {
  return SETTINGS_SAVE_TESTIDS[type] ?? 'settings-save';
}

/* ── the parts ────────────────────────────────────────────────────────────── */

/** monday's 15 px section label: `Symbol`, `Format`, `Scale`, `Color`, `Time presentation`. */
export function SettingsSection({ label }: { label: string }) {
  return <p className="ez-type-settings__section">{label}</p>;
}

export function SettingsDivider() {
  return <hr className="ez-type-settings__divider" />;
}

/**
 * The 288 × 32 segmented group — monday's `button-group`, one 144 px button per option for two,
 * 96 for three. The value is whatever the type stores; the caller never sees an index.
 */
export function SegmentedField<T extends string | number | boolean>({
  label,
  testId,
  value,
  options,
  onChange,
}: {
  label?: string;
  /** Each button is `${testId}-${option.id}`; the group itself is `testId`. */
  readonly testId: string;
  readonly value: T;
  readonly options: readonly { readonly id: string; readonly label: string; readonly value: T }[];
  onChange(next: T): void;
}) {
  return (
    <>
      {label === undefined ? null : <SettingsSection label={label} />}
      <div className="ez-type-settings__group" role="group" aria-label={label ?? testId} data-testid={testId}>
        {options.map((option) => (
          <button
            key={option.id}
            type="button"
            className="ez-type-settings__option"
            data-testid={`${testId}-${option.id}`}
            aria-pressed={option.value === value}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </>
  );
}

/** A 16 px checkbox with monday's optional indented subtitle. */
export function SettingsCheckbox({
  label,
  hint,
  testId,
  checked,
  onChange,
  disabled = false,
}: {
  readonly label: string;
  readonly hint?: string;
  readonly testId: string;
  readonly checked: boolean;
  onChange(next: boolean): void;
  readonly disabled?: boolean;
}) {
  return (
    <>
      <label className="ez-type-settings__check">
        <input
          type="checkbox"
          data-testid={testId}
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
        />
        {label}
      </label>
      {hint === undefined ? null : <p className="ez-type-settings__hint">{hint}</p>}
    </>
  );
}

/** A labelled text / number field. `inline` is monday's right-aligned short field. */
export function SettingsField({
  label,
  testId,
  value,
  onChange,
  type = 'text',
  placeholder,
  min,
  max,
  maxLength,
  inline = false,
}: {
  readonly label: string;
  readonly testId: string;
  readonly value: string;
  onChange(next: string): void;
  readonly type?: 'text' | 'number';
  readonly placeholder?: string;
  readonly min?: number;
  readonly max?: number;
  readonly maxLength?: number;
  readonly inline?: boolean;
}) {
  return (
    <label className={inline ? 'ez-type-settings__control ez-type-settings__control--inline' : 'ez-type-settings__control'}>
      <span>{label}</span>
      <input
        type={type}
        data-testid={testId}
        value={value}
        placeholder={placeholder}
        min={min}
        max={max}
        maxLength={maxLength}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

/** A labelled `<select>` — used where monday's own control is a dropdown menu row. */
export function SettingsSelect({
  label,
  testId,
  value,
  options,
  onChange,
  inline = false,
}: {
  readonly label: string;
  readonly testId: string;
  readonly value: string;
  readonly options: readonly { readonly value: string; readonly label: string }[];
  onChange(next: string): void;
  readonly inline?: boolean;
}) {
  return (
    <label className={inline ? 'ez-type-settings__control ez-type-settings__control--inline' : 'ez-type-settings__control'}>
      <span>{label}</span>
      <select data-testid={testId} value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * monday's colour row (`rating/dialog-settings.png`: nine 24 px circles under `Color`, the picked
 * one wearing a ring). The stored value is the **hex**, which is what `settings.color` holds for
 * `rating`, `checkbox`, `button` and `auto_number` `[cvs§2]`; every entry is one of the account's
 * own 40 status colours, so nothing new is invented here.
 *
 * The nine names are read **off the screenshot, left to right** — dark green, orange (the ringed
 * one), blue, red, green, teal blue, purple, yellow, black — and matched to the nearest palette
 * entry, because the capture's text tree stops at the `Color` label and 24 px circles at 320 px do
 * not give their hexes back. The order and the count are measured; the exact hexes are **ASSUMED**
 * and raised as q517.
 */
export const SETTINGS_COLOR_NAMES: readonly string[] = [
  'grass_green',
  'working_orange',
  'bright_blue',
  'stuck_red',
  'done_green',
  'dark_blue',
  'purple',
  'egg_yolk',
  'blackish',
];

export function ColorSwatches({
  label = 'Color',
  testId,
  value,
  onChange,
  names = SETTINGS_COLOR_NAMES,
}: {
  readonly label?: string;
  readonly testId: string;
  /** The stored hex, or `undefined` when the type has no colour yet. */
  readonly value: string | undefined;
  onChange(hex: string): void;
  readonly names?: readonly string[];
}) {
  return (
    <>
      <SettingsSection label={label} />
      <ul className="ez-type-settings__swatches" data-testid={testId}>
        {names.map((name) => {
          const hex = statusColorHex(name);
          return (
            <li key={name}>
              <button
                type="button"
                className="ez-type-settings__swatch"
                data-testid={`${testId}-${name}`}
                aria-label={name}
                aria-pressed={value?.toLowerCase() === hex.toLowerCase()}
                style={{ backgroundColor: hex, color: hex }}
                onClick={() => onChange(hex)}
              />
            </li>
          );
        })}
      </ul>
    </>
  );
}

/**
 * *Hide the group summary for this column* — `hide_footer`, the one setting **every** type
 * carries (`settings_str.hide_footer`, monday's column menu row *Hide column summary*). It is
 * rendered last in every dialog, under a divider, which is where monday's menu puts it.
 */
export function HideSummaryField({
  testId,
  checked,
  onChange,
  divider = true,
}: {
  readonly testId: string;
  readonly checked: boolean;
  onChange(next: boolean): void;
  /** `false` in the dialogs where it is the only control and there is nothing to divide. */
  readonly divider?: boolean;
}) {
  return (
    <>
      {divider ? <SettingsDivider /> : null}
      <SettingsCheckbox
        label="Hide the group summary for this column"
        testId={testId}
        checked={checked}
        onChange={onChange}
      />
    </>
  );
}
